"use client";

import { forwardRef, type ComponentPropsWithoutRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

type Props = Omit<ComponentPropsWithoutRef<typeof Link>, "href" | "prefetch"> & { href: string };

/**
 * `SITE-NAV01`: a link that fetches its page when the visitor shows intent
 * (the pointer over it, keyboard focus on it, a finger on it), not when it
 * scrolls into view.
 *
 * A plain `<Link>` prefetches every page it can see, so the site header alone
 * started eleven downloads on every page load, 146 KB on the homepage
 * (measured on production 2026-09-25), most of them for pages the visitor
 * never opened, and on a phone they share the line with the page's own
 * first paint. `prefetch={false}` turns that off and, in Next 15, turns off the
 * hover prefetch too (`app-dir/link.js`), so this puts the hover half back by
 * hand. The page still arrives before the click lands on a pointer device: the
 * time between hovering a link and releasing the button is usually longer than
 * a prefetch of 14 to 38 KB.
 */
export const IntentLink = forwardRef<HTMLAnchorElement, Props>(function IntentLink(
  { href, onMouseEnter, onFocus, onTouchStart, ...rest },
  ref,
) {
  const router = useRouter();

  function warm() {
    // The router keeps what it fetched and ignores a repeat inside its cache
    // window, so a second hover costs nothing.
    router.prefetch(href);
  }

  return (
    <Link
      ref={ref}
      href={href}
      prefetch={false}
      onMouseEnter={(event) => {
        warm();
        onMouseEnter?.(event);
      }}
      onFocus={(event) => {
        warm();
        onFocus?.(event);
      }}
      onTouchStart={(event) => {
        warm();
        onTouchStart?.(event);
      }}
      {...rest}
    />
  );
});
